import { CommonModule } from '@angular/common';
import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Observable, Subscription, finalize } from 'rxjs';
import { SUPER_ADMIN_PERMISSION_SLUGS } from 'src/app/core/constants/super-admin-permission-slugs';
import { HasPermissionPipe } from 'src/app/core/pipes/has-permission.pipe';
import {
  BillingApiService,
  InvoiceDashboard,
  InvoiceRecord,
  PaymentHistoryRow,
} from 'src/app/core/services/billing-api.service';
import {
  DEFAULT_LIST_MODULE_FILTERS,
  ListCellDefDirective,
  ListModuleColumnDef,
  ListModuleFilters,
  ListModulePaginationMeta,
  ListModuleSurfaceComponent,
  ListModuleSurfaceConfig,
  ListModuleTableComponent,
} from 'src/app/shared/components/list-module';
import { PdfPreviewModalComponent } from 'src/app/shared/components/pdf-preview-modal/pdf-preview-modal.component';
import { PageSurfaceHeaderComponent } from 'src/app/shared/page-surface-header/page-surface-header.component';
import { InvoiceStatusView, invoiceStatus } from '../invoice-status.util';

const PAGE_SIZE = 20;
const NOWRAP = 'whitespace-nowrap text-sm';

export type InvoicesView = 'all' | 'clients';

/** A client id from the URL; anything that is not a positive integer means "no filter". */
export function parseClientParam(raw: string | null): number | null {
  if (!raw || !/^\d+$/.test(raw)) return null;
  const id = Number(raw);
  return id > 0 ? id : null;
}

/** The single invoice list: every invoice, filterable by client and status, with the actions that follow a state. */
@Component({
  selector: 'app-invoice-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    HasPermissionPipe,
    PageSurfaceHeaderComponent,
    ListModuleSurfaceComponent,
    ListModuleTableComponent,
    ListCellDefDirective,
    PdfPreviewModalComponent,
  ],
  templateUrl: './invoice-dashboard.component.html',
})
export class InvoiceDashboardComponent implements OnInit {
  private readonly api = inject(BillingApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  readonly P = SUPER_ADMIN_PERMISSION_SLUGS;

  view: InvoicesView = 'all';

  dashboard: InvoiceDashboard = { active_businesses: 0, invoice_raised: 0, invoice_collected: 0, businesses: [] };
  dashboardLoading = true;
  dashboardError = '';

  invoices: InvoiceRecord[] = [];
  total = 0;
  page = 1;
  readonly pageSize = PAGE_SIZE;
  loading = false;
  listError = '';
  private listSub?: Subscription;

  clientFilter: number | null = null;
  statusFilter = '';

  actionMsg = '';
  actionErr = '';
  preview: { title: string; filename: string; load: () => Observable<Blob> } | null = null;

  historyOpen = false;
  historyRows: PaymentHistoryRow[] = [];
  historyTotals = { received: 0, tds: 0, total: 0 };
  /** The dialog must not claim "no payments recorded" when the history simply failed to load. */
  historyLoadError = false;

  readonly allSurfaceConfig: ListModuleSurfaceConfig = {
    toolbar: {
      search: false,
      status: {
        label: 'Status',
        options: [
          { value: '', label: 'All statuses' },
          { value: 'DRAFT', label: 'Draft' },
          { value: 'SENT', label: 'Sent' },
          { value: 'PENDING_VERIFICATION', label: 'Awaiting verification' },
          { value: 'PAID', label: 'Paid' },
          { value: 'VOID', label: 'Void' },
        ],
      },
    },
    pagination: true,
  };

  /** The per-client summary arrives in one payload, so there is nothing to page or filter. */
  readonly clientsSurfaceConfig: ListModuleSurfaceConfig = { toolbar: {}, pagination: false };

  readonly columns: ListModuleColumnDef[] = [
    { id: 'number', header: 'Invoice no', cellClass: NOWRAP },
    { id: 'client', header: 'Client' },
    { id: 'issued', header: 'Issued', cellClass: NOWRAP },
    { id: 'due', header: 'Due', cellClass: NOWRAP },
    { id: 'total', header: 'Total', cellClass: NOWRAP },
    { id: 'paid', header: 'Paid', cellClass: NOWRAP },
    { id: 'balance', header: 'Balance', cellClass: NOWRAP },
    { id: 'status', header: 'Status', cellClass: NOWRAP },
    { id: 'actions', header: '', align: 'right' },
  ];

  readonly clientColumns: ListModuleColumnDef[] = [
    { id: 'display_id', header: 'Invoice ID', cellClass: 'whitespace-nowrap' },
    { id: 'total_invoices', header: 'Total Invoices', cellClass: NOWRAP },
    { id: 'business', header: 'Business' },
    { id: 'total', header: 'Total', cellClass: NOWRAP },
    { id: 'issued_date', header: 'Issued Date', cellClass: NOWRAP },
    { id: 'payment_type', header: 'Payment Type', cellClass: NOWRAP },
    { id: 'paid_amount', header: 'Paid Amount', cellClass: NOWRAP },
    { id: 'balance', header: 'Balance', cellClass: NOWRAP },
  ];

  get listFilters(): ListModuleFilters {
    return { ...DEFAULT_LIST_MODULE_FILTERS, status: this.statusFilter };
  }

  get meta(): ListModulePaginationMeta {
    return { total: this.total, lastPage: this.totalPages, currentPage: this.page, perPage: this.pageSize };
  }

  /** A failed list load must not be hidden behind an older action error. */
  get bannerError(): string {
    return this.listError || this.actionErr;
  }

  /** A `?client=` id the summary does not list would otherwise leave the dropdown blank. */
  get clientNotListed(): boolean {
    return this.clientFilter !== null && !this.dashboard.businesses.some((b) => b.tenant_id === this.clientFilter);
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.total / this.pageSize));
  }

  ngOnInit(): void {
    this.loadDashboard();
    // The URL is the source of truth for the client, so a refresh, a redirect and the Open-invoice
    // hand-off from Usage & Charges all land on the same filtered list.
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((query) => {
      this.clientFilter = parseClientParam(query.get('client'));
      this.page = 1;
      this.loadInvoices();
    });
  }

  private loadDashboard(): void {
    this.dashboardError = '';
    this.api.getInvoiceDashboard().subscribe({
      next: (res) => {
        this.dashboard = res.data;
        this.dashboardLoading = false;
      },
      error: () => {
        this.dashboardLoading = false;
        this.dashboardError = 'Could not load the client summary.';
      },
    });
  }

  loadInvoices(): void {
    this.listError = '';
    this.actionMsg = '';
    this.actionErr = '';
    // A slow response for an earlier client or page must not land after a newer one.
    this.listSub?.unsubscribe();
    // Set after unsubscribe: tearing down the old request runs its finalize, which clears loading.
    this.loading = true;
    this.listSub = this.api
      .listInvoices({
        tenant_id: this.clientFilter ?? undefined,
        status: this.statusFilter || undefined,
        page: this.page,
        page_size: this.pageSize,
      })
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (res) => {
          this.invoices = res?.data?.items ?? [];
          this.total = res?.data?.total ?? 0;
        },
        // Fail visibly: on a money screen an empty table with no message reads as "no invoices".
        error: () => {
          this.invoices = [];
          this.total = 0;
          this.listError = 'Could not load invoices.';
        },
      });
  }

  setView(view: InvoicesView): void {
    this.view = view;
  }

  onClientChange(clientId: number | null): void {
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { client: clientId },
      queryParamsHandling: 'merge',
    });
  }

  openBusiness(tenantId: number): void {
    this.view = 'all';
    this.onClientChange(tenantId);
  }

  onListFiltersChange(patch: Partial<ListModuleFilters>): void {
    if (patch.status !== undefined) this.statusFilter = patch.status;
    this.page = 1;
    this.loadInvoices();
  }

  onListPageChange(next: number): void {
    if (next < 1 || next > this.totalPages) return;
    this.page = next;
    this.loadInvoices();
  }

  statusOf(inv: InvoiceRecord): InvoiceStatusView {
    return invoiceStatus(inv);
  }

  private isOpen(inv: InvoiceRecord): boolean {
    return inv.status !== 'PAID' && inv.status !== 'VOID';
  }

  canSend(inv: InvoiceRecord): boolean {
    return inv.status === 'DRAFT' || inv.status === 'SENT';
  }

  canRecordPayment(inv: InvoiceRecord): boolean {
    // Keyed on payment_status, not status: legacy PAID rows can still owe money.
    return inv.status !== 'VOID' && inv.payment_status !== 'PAID';
  }

  /** A part-collected invoice cannot be voided: its payments would stay attached to a void invoice. */
  canVoid(inv: InvoiceRecord): boolean {
    return this.isOpen(inv) && (inv.paid_amount ?? 0) === 0;
  }

  send(inv: InvoiceRecord): void {
    this.runAction(this.api.sendInvoice(inv.id));
  }

  voidInvoice(inv: InvoiceRecord): void {
    this.runAction(this.api.voidInvoice(inv.id));
  }

  recordPayment(inv: InvoiceRecord): void {
    void this.router.navigate(['/invoices/payment', inv.id]);
  }

  previewInvoice(inv: InvoiceRecord): void {
    this.preview = {
      title: `Invoice ${inv.invoice_number}`,
      filename: `${inv.invoice_number}.pdf`,
      load: () => this.api.getInvoicePdf(inv.id),
    };
  }

  closePreview(): void {
    this.preview = null;
  }

  openHistory(invoiceId: number): void {
    this.historyLoadError = false;
    this.api.getPaymentHistory(invoiceId).subscribe({
      next: (res) => {
        this.historyRows = res.data.items;
        this.historyTotals = {
          received: res.data.grand_total_received,
          tds: res.data.grand_total_tds,
          total: res.data.grand_total,
        };
        this.historyOpen = true;
      },
      error: () => {
        this.historyLoadError = true;
        this.historyOpen = true;
      },
    });
  }

  private runAction(obs: Observable<{ message: string }>): void {
    this.actionMsg = '';
    this.actionErr = '';
    obs.subscribe({
      next: (r) => {
        // Reload first: loadInvoices clears the banner, and the success message must survive it.
        this.loadInvoices();
        this.loadDashboard();
        this.actionMsg = r.message;
      },
      error: (err) => (this.actionErr = (err as { error?: { message?: string } })?.error?.message ?? 'Action failed.'),
    });
  }
}
